import { useEffect } from "react";
import { StitchElement } from "@melco/renderer";
import { EfsMelcoModelsDesign } from "melco-shared-logic";
import {
  FusionElementConfigurationChangedAction,
  FusionSetBlankPictureAndDesignAction,
  FusionZoomToFitAction,
} from "./FusionRendererActions";
import {
  ElementConfiguration,
  getRenderResourceByURL,
  useRenderer,
} from "./useRenderer";

export const useDesignRenderer = (
  design: EfsMelcoModelsDesign,
  elementConfiguration: ElementConfiguration
) => {
  const canvasId = `canvas-design-${design.id}`;

  const renderer = useRenderer(canvasId, elementConfiguration);

  const { isLoading, resources, rendererDispatch, canvas } = renderer;

  const rendererDesignElement = design.rfm_url
    ? getRenderResourceByURL(resources, design.rfm_url)
    : undefined;

  useEffect(() => {
    if (isLoading || !rendererDesignElement) {
      return;
    }

    rendererDispatch(
      new FusionSetBlankPictureAndDesignAction(
        undefined,
        rendererDesignElement as StitchElement
      )
    );
  }, [isLoading, rendererDesignElement, rendererDispatch]);

  useEffect(() => {
    if (isLoading || !rendererDesignElement) {
      return;
    }

    rendererDispatch(
      new FusionElementConfigurationChangedAction(
        rendererDesignElement as StitchElement,
        elementConfiguration,
        resources
      )
    );
  }, [
    isLoading,
    resources,
    rendererDesignElement,
    elementConfiguration,
    rendererDispatch,
  ]);

  // handle canvas size changes
  useEffect(() => {
    if (canvas && renderer.renderer) {
      const resizeObserver = new ResizeObserver(() => {
        if (renderer.renderer) {
          renderer.renderer.handleCanvasSizeChanged();
        }

        // force recalculation of new viewport
        const { clientWidth, clientHeight } = canvas;

        if (renderer.elementUtil) {
          rendererDispatch(
            new FusionZoomToFitAction(
              undefined,
              clientWidth,
              clientHeight,
              renderer.elementUtil
            )
          );
        }
      });

      resizeObserver.observe(canvas);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [
    canvas,
    renderer.renderer,
    renderer.elementUtil,
    rendererDispatch,
    rendererDesignElement,
  ]);

  return renderer;
};
